import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Container, Form, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from '../axiosAlertConfig';
import { UserContext } from '../../userAlertContext';
import { locationToPath } from '../../utils/incidentDeepLink';
import consentPdf from './SchoolShieldOptInWorkflow.pdf';

const DOCUMENT_VERSION = 'SchoolShieldOptInWorkflow-2026-02-23';
const PDF_VIEWER_ZOOM = '#page=1&zoom=100';

export default function IncidentAlertConsentPage() {
  const { user, setUser } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [accepted, setAccepted] = useState(false);
  const [stopAll, setStopAll] = useState(false);
  const [smsOptOut, setSmsOptOut] = useState(false);
  const [voiceOptOut, setVoiceOptOut] = useState(false);
  const [emailOptOut, setEmailOptOut] = useState(false);
  const [error, setError] = useState('');

  const accountID = user?.iAccountID || user?.accountID || '';
  const userID = user?.userID || user?.iUserID || '';

  const rawReturnTo = locationToPath(location.state?.from) || '/reportincidentpanel';

  const returnTo =
    rawReturnTo === '/incident-alert-consent' ||
    rawReturnTo === '/incident-alert-preferences'
      ? '/reportincidentpanel'
      : rawReturnTo;

  const effectiveSmsOptOut = stopAll || smsOptOut;
  const effectiveVoiceOptOut = stopAll || voiceOptOut;
  const effectiveEmailOptOut = stopAll || emailOptOut;

  const isPreferencesPage = location.pathname === '/incident-alert-preferences';

  const pageTitle = useMemo(() => (
    isPreferencesPage
      ? 'Incident Alert Message Preferences'
      : 'Incident Alert Message Consent'
  ), [isPreferencesPage]);

  useEffect(() => {
    if (!accountID || !userID) return;

    axios.get(`/UsersManagement/users/${userID}/incident-alert-consent`, {
      params: { iAccountID: accountID },
    })
      .then(({ data }) => {
        const u = data?.user || {};
        const sms = Number(u.incidentAlertSmsOptOut || 0) === 1;
        const voice = Number(u.incidentAlertVoiceOptOut || 0) === 1;
        const email = Number(u.incidentAlertEmailOptOut || 0) === 1;

        setAccepted(Number(u.incidentAlertOptInAccepted || 0) === 1);
        setSmsOptOut(sms);
        setVoiceOptOut(voice);
        setEmailOptOut(email);
        setStopAll(sms && voice && email);
      })
      .catch(() => setError('Unable to load Incident Alert preferences.'))
      .finally(() => setLoading(false));
  }, [accountID, userID]);

 const cancel = () => {
  setUser?.(null);
  sessionStorage.removeItem('userInfo');
  navigate('/login', { replace: true });
};

  const save = async () => {
    if (!accepted) {
      setError('You must read and accept the SchoolShield emergency alert consent document to continue.');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const acceptRes = await axios.put(
        `/UsersManagement/users/${userID}/incident-alert-consent/accept`,
        {
          iAccountID: accountID,
          documentVersion: DOCUMENT_VERSION,
        }
      );

      const prefsRes = await axios.put(
        `/UsersManagement/users/${userID}/incident-alert-consent/preferences`,
        {
          iAccountID: accountID,
          stopAll,
          smsOptOut: effectiveSmsOptOut,
          voiceOptOut: effectiveVoiceOptOut,
          emailOptOut: effectiveEmailOptOut,
        }
      );

 const parseJsonArray = (value, fallback = []) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : fallback;
    } catch {
      return fallback;
    }
  }
  return fallback;
};

const acceptUser = acceptRes.data?.user || {};
const prefsUser = prefsRes.data?.user || {};

const nextUser = {
  ...user,
  ...acceptUser,
  ...prefsUser,
  incidentAlertOptInAccepted: 1,

  // preserve array format for the rest of Incident Alert
  campusIDs: parseJsonArray(
    prefsUser.campusIDs ?? acceptUser.campusIDs,
    user?.campusIDs || []
  ),
  campusNames: parseJsonArray(
    prefsUser.campusNames ?? acceptUser.campusNames,
    user?.campusNames || []
  ),
};

      setUser?.(nextUser);
      sessionStorage.setItem('userInfo', JSON.stringify(nextUser));

      navigate(returnTo, { replace: true });
    } catch (e) {
      setError(e?.response?.data?.error || 'Unable to save Incident Alert preferences.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-4 text-center">
        <Spinner animation="border" size="sm" />
      </Container>
    );
  }

  return (

<>
  <style>
    {`
      .darkConsentCheckbox .form-check-input {
        border: 2px solid #111827;
        width: 1.15rem;
        height: 1.15rem;
      }

      .darkConsentCheckbox .form-check-input:checked {
        background-color: #0b2f6a;
        border-color: #0b2f6a;
      }

      .darkConsentCheckbox .form-check-label {
        color: #111827;
        font-weight: 600;
      }
    `}
  </style>

 

    <Container className="py-4" style={{ maxWidth: 950 }}>
      <Card>
        <Card.Header>
          <strong>{pageTitle}</strong>
        </Card.Header>

        <Card.Body>
          {error ? <Alert variant="danger">{error}</Alert> : null}

          <Alert variant="warning" className="mb-3">
            Read the SchoolShield emergency alert consent document below. You must accept it before using Incident Alert messaging.
          </Alert>

          <div
            className="border rounded mb-3"
            style={{
              height: '60vh',
              minHeight: 420,
              overflow: 'hidden',
              background: '#f8f9fa',
            }}
          >
            <iframe
  src={`${consentPdf}${PDF_VIEWER_ZOOM}`}
  title="SchoolShield Emergency Alert Consent Document"
  style={{
    width: '100%',
    height: '100%',
    border: 0,
  }}
/>
          </div>

          <div className="mb-3">
            <a href={consentPdf} target="_blank" rel="noopener noreferrer">
              Open consent document in a new tab
            </a>
          </div>

 <Form.Check
  className="mb-3 darkConsentCheckbox"
  type="checkbox"
  checked={accepted}
  onChange={(e) => setAccepted(e.target.checked)}
  label="I have read and agree to the SchoolShield emergency alert consent document, Terms of Service, and Privacy Policy."
/>

          <hr />

 
 
        </Card.Body>

 <Card.Footer>
  <div className="d-flex justify-content-end gap-2">
    <Button
      variant="secondary"
      type="button"
      onClick={cancel}
      disabled={saving}
    >
      Cancel
    </Button>

    <Button
      type="button"
      onClick={save}
      disabled={saving}
    >
      {saving ? 'Saving…' : 'Save and Continue'}
    </Button>
  </div>
</Card.Footer>
      </Card>
    </Container>

    </>
  );
}

// import React, { useContext, useEffect, useMemo, useState } from 'react';
// import { Alert, Button, Card, Container, Form, Spinner } from 'react-bootstrap';
// import { useLocation, useNavigate } from 'react-router-dom';
// import axios from '../axiosAlertConfig';
// import { UserContext } from '../../userAlertContext';
// import consentPdf from './SchoolShieldOptInWorkflow.pdf';

// const DOCUMENT_VERSION = 'SchoolShieldOptInWorkflow-2026-02-23';
// const PDF_VIEWER_ZOOM = '#page=1&zoom=100';

// export default function IncidentAlertConsentPage() {
//   const { user, setUser } = useContext(UserContext);
//   const navigate = useNavigate();
//   const location = useLocation();

//   const [loading, setLoading] = useState(true);
//   const [saving, setSaving] = useState(false);

//   const [accepted, setAccepted] = useState(false);
//   const [stopAll, setStopAll] = useState(false);
//   const [smsOptOut, setSmsOptOut] = useState(false);
//   const [voiceOptOut, setVoiceOptOut] = useState(false);
//   const [emailOptOut, setEmailOptOut] = useState(false);
//   const [error, setError] = useState('');

//   const accountID = user?.iAccountID || user?.accountID || '';
//   const userID = user?.userID || user?.iUserID || '';

//    const rawReturnTo = location.state?.from?.pathname || '/reportincidentpanel';

// const returnTo =
//   rawReturnTo === '/incident-alert-consent' ||
//   rawReturnTo === '/incident-alert-preferences'
//     ? '/reportincidentpanel'
//     : rawReturnTo;

//   const effectiveSmsOptOut = stopAll || smsOptOut;
//   const effectiveVoiceOptOut = stopAll || voiceOptOut;
//   const effectiveEmailOptOut = stopAll || emailOptOut;

//   const isPreferencesPage = location.pathname === '/incident-alert-preferences';

//   const pageTitle = useMemo(() => (
//     isPreferencesPage
//       ? 'Incident Alert Message Preferences'
//       : 'Incident Alert Message Consent'
//   ), [isPreferencesPage]);

//   useEffect(() => {
//     if (!accountID || !userID) return;

//     axios.get(`/UsersManagement/users/${userID}/incident-alert-consent`, {
//       params: { iAccountID: accountID },
//     })
//       .then(({ data }) => {
//         const u = data?.user || {};
//         const sms = Number(u.incidentAlertSmsOptOut || 0) === 1;
//         const voice = Number(u.incidentAlertVoiceOptOut || 0) === 1;
//         const email = Number(u.incidentAlertEmailOptOut || 0) === 1;

//         setAccepted(Number(u.incidentAlertOptInAccepted || 0) === 1);
//         setSmsOptOut(sms);
//         setVoiceOptOut(voice);
//         setEmailOptOut(email);
//         setStopAll(sms && voice && email);
//       })
//       .catch(() => setError('Unable to load Incident Alert preferences.'))
//       .finally(() => setLoading(false));
//   }, [accountID, userID]);

//  const cancel = () => {
//   setUser?.(null);
//   sessionStorage.removeItem('userInfo');
//   navigate('/login', { replace: true });
// };

//   const save = async () => {
//     if (!accepted) {
//       setError('You must read and accept the SchoolShield emergency alert consent document to continue.');
//       return;
//     }

//     try {
//       setSaving(true);
//       setError('');

//       const acceptRes = await axios.put(
//         `/UsersManagement/users/${userID}/incident-alert-consent/accept`,
//         {
//           iAccountID: accountID,
//           documentVersion: DOCUMENT_VERSION,
//         }
//       );

//       const prefsRes = await axios.put(
//         `/UsersManagement/users/${userID}/incident-alert-consent/preferences`,
//         {
//           iAccountID: accountID,
//           stopAll,
//           smsOptOut: effectiveSmsOptOut,
//           voiceOptOut: effectiveVoiceOptOut,
//           emailOptOut: effectiveEmailOptOut,
//         }
//       );

//  const parseJsonArray = (value, fallback = []) => {
//   if (Array.isArray(value)) return value;
//   if (typeof value === 'string') {
//     try {
//       const parsed = JSON.parse(value);
//       return Array.isArray(parsed) ? parsed : fallback;
//     } catch {
//       return fallback;
//     }
//   }
//   return fallback;
// };

// const acceptUser = acceptRes.data?.user || {};
// const prefsUser = prefsRes.data?.user || {};

// const nextUser = {
//   ...user,
//   ...acceptUser,
//   ...prefsUser,
//   incidentAlertOptInAccepted: 1,

//   // preserve array format for the rest of Incident Alert
//   campusIDs: parseJsonArray(
//     prefsUser.campusIDs ?? acceptUser.campusIDs,
//     user?.campusIDs || []
//   ),
//   campusNames: parseJsonArray(
//     prefsUser.campusNames ?? acceptUser.campusNames,
//     user?.campusNames || []
//   ),
// };

//       setUser?.(nextUser);
//       sessionStorage.setItem('userInfo', JSON.stringify(nextUser));

//       navigate(returnTo, { replace: true });
//     } catch (e) {
//       setError(e?.response?.data?.error || 'Unable to save Incident Alert preferences.');
//     } finally {
//       setSaving(false);
//     }
//   };

//   if (loading) {
//     return (
//       <Container className="py-4 text-center">
//         <Spinner animation="border" size="sm" />
//       </Container>
//     );
//   }

//   return (

// <>
//   <style>
//     {`
//       .darkConsentCheckbox .form-check-input {
//         border: 2px solid #111827;
//         width: 1.15rem;
//         height: 1.15rem;
//       }

//       .darkConsentCheckbox .form-check-input:checked {
//         background-color: #0b2f6a;
//         border-color: #0b2f6a;
//       }

//       .darkConsentCheckbox .form-check-label {
//         color: #111827;
//         font-weight: 600;
//       }
//     `}
//   </style>

 

//     <Container className="py-4" style={{ maxWidth: 950 }}>
//       <Card>
//         <Card.Header>
//           <strong>{pageTitle}</strong>
//         </Card.Header>

//         <Card.Body>
//           {error ? <Alert variant="danger">{error}</Alert> : null}

//           <Alert variant="warning" className="mb-3">
//             Read the SchoolShield emergency alert consent document below. You must accept it before using Incident Alert messaging.
//           </Alert>

//           <div
//             className="border rounded mb-3"
//             style={{
//               height: '60vh',
//               minHeight: 420,
//               overflow: 'hidden',
//               background: '#f8f9fa',
//             }}
//           >
//             <iframe
//   src={`${consentPdf}${PDF_VIEWER_ZOOM}`}
//   title="SchoolShield Emergency Alert Consent Document"
//   style={{
//     width: '100%',
//     height: '100%',
//     border: 0,
//   }}
// />
//           </div>

//           <div className="mb-3">
//             <a href={consentPdf} target="_blank" rel="noopener noreferrer">
//               Open consent document in a new tab
//             </a>
//           </div>

//  <Form.Check
//   className="mb-3 darkConsentCheckbox"
//   type="checkbox"
//   checked={accepted}
//   onChange={(e) => setAccepted(e.target.checked)}
//   label="I have read and agree to the SchoolShield emergency alert consent document, Terms of Service, and Privacy Policy."
// />

//           <hr />

 
 
//         </Card.Body>

//  <Card.Footer>
//   <div className="d-flex justify-content-end gap-2">
//     <Button
//       variant="secondary"
//       type="button"
//       onClick={cancel}
//       disabled={saving}
//     >
//       Cancel
//     </Button>

//     <Button
//       type="button"
//       onClick={save}
//       disabled={saving}
//     >
//       {saving ? 'Saving…' : 'Save and Continue'}
//     </Button>
//   </div>
// </Card.Footer>
//       </Card>
//     </Container>

//     </>
//   );
// }